import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './GroupsDropdown.css';
import '../sidebar/Home.css';
import LoadingOverlay from '../components/LoadingOverlay';
import FormularioPostagem from '../Formulario/FormularioPostagem';
import { extractPostDate, formatPostDate, formatPostDateMinutes } from '../utils/dateUtils';
import Lightbox from '../components/Lightbox';
import { getAuthToken, getUsuarioSistemaId } from '../utils/auth';
import MembrosGrupoPopup from '../Modal/MembrosGrupoPopup';
import GerenciarAdminPopup from '../Modal/GerenciarAdminPopup';
import BASE_URL from '../utils/api';

// Simple API helper using only the working 7040 base
const apiCall = async (endpoint, method = 'GET', data = null) => {
  const bases = [
    `/api/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
  ];
  for (const base of bases) {
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const cfg = { method, headers };
      if (data && (method === 'POST' || method === 'PUT')) cfg.body = JSON.stringify(data);
      const res = await fetch(base, cfg);
      if (!res) continue;
      if (res.ok || (res.status >= 200 && res.status < 300)) {
        const ct = res.headers.get('Content-Type') || '';
        const text = await res.text();
        if (ct.includes('application/json')) {
          try { return JSON.parse(text); } catch { return text; }
        }
        return text;
      }
    } catch (e) { /* try next base */ }
  }
  throw new Error(`Nenhuma URL funcionou para ${method} ${endpoint}`);
};

const apiUploadMultipart = async (endpoint, formData) => {
  const bases = ['', BASE_URL.replace(/\/api\/?$/, '')];
  let lastErr = null;
  for (const base of bases) {
    const url = `${base}/api/${endpoint}`.replace('//api/api', '/api');
    try {
      const headers = {};
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(url, { method: 'POST', body: formData, headers });
      if (!res.ok) { lastErr = new Error(`${res.status} ${res.statusText}`); continue; }
      const text = await res.text();
      try { return JSON.parse(text); } catch { return text; }
    } catch (err) { lastErr = err; continue; }
  }
  throw lastErr || new Error('Falha ao enviar arquivos');
};

export default function GrupoDropDown({ onClose, onGroupSelect, selectedGroup: selectedGroupProp, selectedGroupId }) {
  const [selected, setSelected] = useState(null);
  const [title, setTitle] = useState('Nome do Grupo');
  const [desc, setDesc] = useState('Descrição do grupo');

  // Estados para popups de membros
  const [showMembrosPopup, setShowMembrosPopup] = useState(false);
  const [showGerenciarAdminPopup, setShowGerenciarAdminPopup] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [donoId, setDonoId] = useState(null);

  // Cache de nomes de usuários por UsuarioSistemaId
  const [userNamesById, setUserNamesById] = useState({});
  const userNamesFetchingRef = useRef(new Set());

  // Busca o nome do usuário via DadosUsuario/por-usuario/{usuarioSistemaId}
  const fetchUserName = async (usuarioSistemaId) => {
    if (!usuarioSistemaId || userNamesById[usuarioSistemaId] || userNamesFetchingRef.current.has(usuarioSistemaId)) return;
    userNamesFetchingRef.current.add(usuarioSistemaId);
    try {
      const data = await apiCall(`DadosUsuario/por-usuario/${usuarioSistemaId}`, 'GET');
      const nome = data?.NomeUsuario || data?.nomeUsuario || data?.Nome || data?.nome || null;
      if (nome) {
        setUserNamesById(prev => ({ ...prev, [usuarioSistemaId]: nome }));
      }
    } catch (err) {
      console.debug('Erro ao buscar nome do usuário', usuarioSistemaId, err);
    } finally {
      userNamesFetchingRef.current.delete(usuarioSistemaId);
    }
  };

  const getUserName = (usuarioSistemaId) => {
    if (!usuarioSistemaId) return null;
    return userNamesById[usuarioSistemaId] || null;
  };

  const [likesCountByPost, setLikesCountByPost] = useState({});
  const [likesLoadingByPost, setLikesLoadingByPost] = useState(new Set());
  const [myLikeIdByPost, setMyLikeIdByPost] = useState({});
  const [commentsCountByPost, setCommentsCountByPost] = useState({});

  const fetchCountsForPosts = async (postsArray) => {
    if (!postsArray || postsArray.length === 0) return;
    await Promise.all(postsArray.map(async (mp) => {
      const pid = mp.Id || mp.id || mp.PostagemId || mp.postagemId;
      if (!pid) return;
      try {
        const likeRes = await apiCall(`PostagemCurtida/postagem/${pid}`, 'GET');
        if (likeRes && typeof likeRes.count === 'number') setLikesCountByPost(prev => ({ ...prev, [pid]: likeRes.count }));
        else if (Array.isArray(likeRes)) setLikesCountByPost(prev => ({ ...prev, [pid]: likeRes.length }));
        // if server returns items, try to find an anonymous (UsuarioId null) like and map it as our local like id
        if (likeRes && likeRes.items && Array.isArray(likeRes.items)) {
          const anon = likeRes.items.find(i => i.UsuarioId === null || i.usuarioId === null || i.UsuarioId === undefined);
          if (anon && (anon.Id || anon.id)) setMyLikeIdByPost(prev => ({ ...prev, [pid]: anon.Id || anon.id }));
        }
      } catch (e) { setLikesCountByPost(prev => ({ ...prev, [pid]: 0 })); }
      try {
        const comRes = await apiCall(`PostagemComentario/postagem/${pid}`, 'GET');
        if (Array.isArray(comRes)) setCommentsCountByPost(prev => ({ ...prev, [pid]: comRes.length }));
        else if (comRes && typeof comRes.count === 'number') setCommentsCountByPost(prev => ({ ...prev, [pid]: comRes.count }));
      } catch (e) { setCommentsCountByPost(prev => ({ ...prev, [pid]: 0 })); }
    }));
  };

  // Comments UI state for group panel
  const [commentsOpenPosts, setCommentsOpenPosts] = useState(new Set());
  const [commentsByPost, setCommentsByPost] = useState({});
  const [commentsLoadingPosts, setCommentsLoadingPosts] = useState(new Set());
  const [commentInputs, setCommentInputs] = useState({});
  const [likedPosts, setLikedPosts] = useState(new Set());

  const toggleComments = async (p) => {
    const id = p.Id || p.id || null;
    if (!id) return;
    const isOpen = commentsOpenPosts.has(id);
    if (!isOpen) {
      setCommentsLoadingPosts(prev => new Set(prev).add(id));
      try {
        const data = await apiCall(`PostagemComentario/postagem/${id}`, 'GET');
        const arr = Array.isArray(data) ? data : [];
        setCommentsByPost(prev => ({ ...prev, [id]: arr }));
        setCommentsCountByPost(prev => ({ ...prev, [id]: arr.length }));
      } catch (err) {
        console.error('Erro ao carregar comentários (grupo)', err);
        setCommentsByPost(prev => ({ ...prev, [id]: [] }));
        setCommentsCountByPost(prev => ({ ...prev, [id]: 0 }));
      } finally {
        setCommentsLoadingPosts(prev => { const s = new Set(prev); s.delete(id); return s; });
      }
    }
    setCommentsOpenPosts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleLike = async (p) => {
    const pid = p.Id || p.id || p.PostagemId || p.postagemId;
    if (!pid) return;
    const isLiked = likedPosts.has(pid);
    setLikedPosts(prev => { const s = new Set(prev); isLiked ? s.delete(pid) : s.add(pid); return s; });
    setLikesCountByPost(prev => ({ ...prev, [pid]: Math.max(0, (prev[pid] || 0) + (isLiked ? -1 : 1)) }));
    if (!isLiked) {
      try {
        const created = await apiCall('PostagemCurtida', 'POST', { PostagemId: pid });
        const createdId = created?.Id || created?.id || null;
        if (createdId) setMyLikeIdByPost(prev => ({ ...prev, [pid]: createdId }));
      } catch (e) {
        setLikedPosts(prev => { const s = new Set(prev); s.delete(pid); return s; });
        setLikesCountByPost(prev => ({ ...prev, [pid]: Math.max(0, (prev[pid] || 1) - 1) }));
      }
    } else {
      const likeId = myLikeIdByPost[pid];
      if (likeId) {
        try { await apiCall(`PostagemCurtida/${likeId}`, 'DELETE'); setMyLikeIdByPost(prev => { const c = { ...prev }; delete c[pid]; return c; }); }
        catch (e) { setLikedPosts(prev => { const s = new Set(prev); s.add(pid); return s; }); setLikesCountByPost(prev => ({ ...prev, [pid]: (prev[pid] || 0) + 1 })); }
      }
    }
  };

  const submitComment = async (pid) => {
    const text = (commentInputs[pid] || '').trim();
    if (!text) return;
    try {
      const res = await apiCall('PostagemComentario', 'POST', { PostagemId: pid, Conteudo: text });
      setCommentsByPost(prev => ({ ...prev, [pid]: [...(prev[pid] || []), res] }));
      setCommentsCountByPost(prev => ({ ...prev, [pid]: (prev[pid] || 0) + 1 }));
      setCommentInputs(prev => ({ ...prev, [pid]: '' }));
    } catch (e) { console.error('Erro ao enviar comentário', e); }
  };

  // ── Post edit/delete state ──
  const [editingPostId, setEditingPostId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [editingSaving, setEditingSaving] = useState(false);
  const [confirmDeletePostId, setConfirmDeletePostId] = useState(null);
  const [editImages, setEditImages] = useState([]);
  const [editFiles, setEditFiles] = useState([]);
  const [editNewImages, setEditNewImages] = useState([]);
  const [editNewFiles, setEditNewFiles] = useState([]);
  const [editRemovedFileIds, setEditRemovedFileIds] = useState([]);
  const editImageInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  const startEditPost = (p) => {
    const pid = p.Id || p.id;
    setEditingPostId(pid);
    setEditingText(p.Descricao || p.descricao || p.description || '');
    const allFiles = p.files || [];
    const imgs = allFiles.filter(f => f && f.type && (f.type.startsWith('image/') || f.type.startsWith('video/')));
    const others = allFiles.filter(f => f && !(f.type && (f.type.startsWith('image/') || f.type.startsWith('video/'))));
    setEditImages(imgs.map(f => ({ ...f, isNew: false })));
    setEditFiles(others.map(f => ({ ...f, isNew: false })));
    setEditNewImages([]);
    setEditNewFiles([]);
    setEditRemovedFileIds([]);
  };

  const cancelEditPost = () => {
    setEditingPostId(null);
    setEditingText('');
    setEditImages([]);
    setEditFiles([]);
    setEditNewImages([]);
    setEditNewFiles([]);
    setEditRemovedFileIds([]);
  };

  const saveEditPost = async (postId) => {
    setEditingSaving(true);
    try {
      await apiCall(`Postagem/${postId}`, 'PUT', { Descricao: editingText });
      for (const fId of editRemovedFileIds) {
        try { await apiCall(`PostagemArquivo/${fId}`, 'DELETE'); } catch (e) { console.warn('Erro ao deletar arquivo', fId, e); }
      }
      for (const file of editNewImages) {
        const fd = new FormData();
        fd.append('File', file);
        fd.append('PostagemId', String(postId));
        try { await apiUploadMultipart('PostagemArquivo', fd); } catch (e) { console.warn('Erro ao enviar imagem', e); }
      }
      for (const file of editNewFiles) {
        const fd = new FormData();
        fd.append('File', file);
        fd.append('PostagemId', String(postId));
        try { await apiUploadMultipart('PostagemArquivo', fd); } catch (e) { console.warn('Erro ao enviar arquivo', e); }
      }
      const currentPost = posts.find(pp => (pp.Id || pp.id) == postId);
      if (currentPost) {
        const enriched = await fetchFilesForPost({ ...currentPost, Descricao: editingText, descricao: editingText, description: editingText });
        setPosts(prev => prev.map(pp => (pp.Id || pp.id) == postId ? enriched : pp));
      } else {
        setPosts(prev => prev.map(pp => {
          const pid = pp.Id || pp.id;
          if (pid == postId) return { ...pp, Descricao: editingText, descricao: editingText, description: editingText };
          return pp;
        }));
      }
      cancelEditPost();
    } catch (err) {
      console.error('Erro ao editar postagem:', err);
      alert('Erro ao salvar edição: ' + (err.message || err));
    } finally {
      setEditingSaving(false);
    }
  };

  const editRemoveImage = (index) => {
    const img = editImages[index];
    if (img && img.id && !img.isNew) setEditRemovedFileIds(prev => [...prev, img.id]);
    if (img && img.isNew) {
      setEditNewImages(prev => prev.filter((_, i) => { return editImages.filter(ei => ei.isNew)[i]?._tempUrl !== img._tempUrl; }));
    }
    setEditImages(prev => prev.filter((_, i) => i !== index));
  };

  const editRemoveFile = (index) => {
    const f = editFiles[index];
    if (f && f.id && !f.isNew) setEditRemovedFileIds(prev => [...prev, f.id]);
    if (f && f.isNew) {
      setEditNewFiles(prev => prev.filter((_, i) => { return editFiles.filter(ef => ef.isNew)[i]?._tempUrl !== f._tempUrl; }));
    }
    setEditFiles(prev => prev.filter((_, i) => i !== index));
  };

  const editAddImages = (fileList) => {
    const newFiles = Array.from(fileList);
    const newEntries = newFiles.map(file => {
      const url = URL.createObjectURL(file);
      return { id: null, url, type: file.type, name: file.name, isNew: true, _tempUrl: url, file };
    });
    setEditImages(prev => [...prev, ...newEntries]);
    setEditNewImages(prev => [...prev, ...newFiles]);
  };

  const editAddFiles = (fileList) => {
    const newFiles = Array.from(fileList);
    const newEntries = newFiles.map(file => {
      const url = URL.createObjectURL(file);
      return { id: null, url, type: file.type, name: file.name, NomeArquivo: file.name, isNew: true, _tempUrl: url, file };
    });
    setEditFiles(prev => [...prev, ...newEntries]);
    setEditNewFiles(prev => [...prev, ...newFiles]);
  };

  const deletePost = async (postId) => {
    try {
      await apiCall(`Postagem/${postId}`, 'DELETE');
      setPosts(prev => prev.filter(p => (p.Id || p.id) != postId));
    } catch (err) {
      console.error('Erro ao excluir postagem:', err);
      alert('Erro ao excluir postagem: ' + (err.message || err));
    } finally {
      setConfirmDeletePostId(null);
    }
  };

  // editing state
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');

  const location = useLocation();

  const [imageUrl, setImageUrl] = useState(null);
  const fileRef = useRef(null);
  const objectUrlRef = useRef(null);
  const [groupLoading, setGroupLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // posts
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState('');

  // sorting controls (mirror Home.js)
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = Mais recente, 'asc' = Mais antigo, 'date' = selecionar data
  const [dateFilter, setDateFilter] = useState('');
  const dateInputRef = useRef(null);

  // per-post image index for slideshow-like navigation
  const [imageIndexes, setImageIndexes] = useState({});
  const postObjectUrlRefs = useRef({});
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lbImages, setLbImages] = useState([]);
  const [lbIndex, setLbIndex] = useState(0);
  const openLightbox = (images, idx = 0) => { setLbImages(images || []); setLbIndex(typeof idx === 'number' ? idx : 0); setLightboxOpen(true); };
  const closeLightbox = () => setLightboxOpen(false);
  const lbNext = () => setLbIndex(i => (i + 1) % Math.max(1, (lbImages || []).length));
  const lbPrev = () => setLbIndex(i => (i - 1 + Math.max(1, (lbImages || []).length)) % Math.max(1, (lbImages || []).length));

  const setImageIndexForPost = (postId, idx) => {
    setImageIndexes(prev => ({ ...prev, [postId]: idx }));
  };

  const nextImage = (postId) => {
    setImageIndexes(prev => {
      const current = prev[postId] || 0;
      const p = posts.find(x => (x.Id || x.id) == postId);
      const len = (p && p.files && p.files.length) || 0;
      return { ...prev, [postId]: (current + 1) % Math.max(1, len) };
    });
  };
  const prevImage = (postId) => {
    setImageIndexes(prev => {
      const current = prev[postId] || 0;
      const p = posts.find(x => (x.Id || x.id) == postId);
      const len = (p && p.files && p.files.length) || 0;
      return { ...prev, [postId]: (current - 1 + Math.max(1, len)) % Math.max(1, len) };
    });
  };

  const fetchFileBlobUrl = async (fileId) => {
    const token = getAuthToken();
    const bases = [BASE_URL.replace(/\/api\/?$/, '')];
    for (const base of bases) {
      try {
        const res = await fetch(`${base}/api/PostagemArquivo/${fileId}`, { headers: token ? { 'Authorization': `Bearer ${token}` } : {} });
        if (res.ok) { const blob = await res.blob(); return URL.createObjectURL(blob); }
      } catch (e) { continue; }
    }
    return null;
  };

  const fetchFilesForPost = async (p) => {
    try {
      const pid = p.Id || p.id || p.PostagemId || p.postagemId;
      if (!pid) return p;
      const fa = await apiCall(`PostagemArquivo/postagem/${pid}`, 'GET');
      const arrFiles = Array.isArray(fa) ? fa : [];
      let files = (await Promise.all((arrFiles || []).map(async f => {
        const type = f.TipoArquivo || f.tipoArquivo || f.tipo || 'application/octet-stream';
        let url = null;
        if (typeof f.Arquivo === 'string' && f.Arquivo.trim().length > 0) {
          url = `data:${type};base64,` + f.Arquivo;
        } else if (Array.isArray(f.Arquivo) && f.Arquivo.length) {
          const b = Uint8Array.from(f.Arquivo);
          url = URL.createObjectURL(new Blob([b], { type }));
          try { postObjectUrlRefs.current[pid] = postObjectUrlRefs.current[pid] || []; postObjectUrlRefs.current[pid].push(url); } catch (e) {}
        }
        if (!url && (f.Id || f.id)) {
          try { url = await fetchFileBlobUrl(f.Id || f.id); } catch (e) { url = null; }
        }
        const fileName = f.NomeArquivo || f.nomeArquivo || f.Nome || f.nome || f.fileName || f.file || f.name || '';
        return { id: f.Id || f.id || null, type, url, name: fileName, NomeArquivo: f.NomeArquivo || f.nomeArquivo || f.Nome || f.nome };
      }))).filter(Boolean);

      const detectOrientation = (url) => new Promise(resolve => {
        try {
          const img = new Image();
          img.onload = () => {
            const w = img.naturalWidth, h = img.naturalHeight;
            const ratio = Math.abs(h - w) / Math.max(h, w, 1);
            const isSquare = ratio < 0.05;
            resolve({ isPortrait: !isSquare && h > w, isSquare });
          };
          img.onerror = () => resolve({ isPortrait: false, isSquare: false });
          img.src = url;
        } catch (e) { resolve({ isPortrait: false, isSquare: false }); }
      });

      files = await Promise.all(files.map(async (f) => {
        if (f.type && f.type.startsWith('image/') && f.url) {
          try { const o = await detectOrientation(f.url); f.isPortrait = o.isPortrait; f.isSquare = o.isSquare; }
          catch (e) { f.isPortrait = false; f.isSquare = false; }
        } else { f.isPortrait = false; f.isSquare = false; }
        return f;
      }));
      return { ...p, files };
    } catch (e) { return { ...p, files: [] }; }
  };

  const sortedGroupPosts = React.useMemo(() => {
    let copy = (posts || []).slice();

    if (sortOrder === 'date' && dateFilter) {
      // compare by local YYYY-MM-DD to avoid timezone/parsing differences
      const selectedYmd = dateFilter;
      const ymdOf = (raw) => {
        if (!raw) return null;
        const d = new Date(raw);
        if (isNaN(d.getTime())) return null;
        const y = d.getFullYear();
        const m = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      copy = copy.filter(p => {
        const raw = p.createdAt || p.DataHora || p.dataHora || p.DataCriacao || p.dataCriacao || p.created_at || p.Data || p.DataPublicacao;
        const ymd = ymdOf(raw);
        return ymd === selectedYmd;
      });
      copy.sort((a, b) => new Date(b.createdAt || b.DataHora || b.dataHora || b.DataCriacao || b.dataCriacao || b.created_at || 0) - new Date(a.createdAt || a.DataHora || a.dataHora || a.DataCriacao || a.dataCriacao || a.created_at || 0));
      return copy;
    }

    copy.sort((a, b) => {
      const da = new Date(a.createdAt || a.DataCriacao || a.dataCriacao || a.created_at || 0);
      const db = new Date(b.createdAt || b.DataCriacao || b.dataCriacao || b.created_at || 0);
      return sortOrder === 'desc' ? db - da : da - db;
    });

    return copy;
  }, [posts, sortOrder, dateFilter]);

  // If a group is passed via props or location state, load it on mount
  useEffect(() => {
    const incomingFromHistory = (typeof window !== 'undefined' && window.history && window.history.state && window.history.state.selectedGroup) ? window.history.state.selectedGroup : null;
    const incomingFromLocation = (location && location.state && location.state.selectedGroup) ? location.state.selectedGroup : null;
    const startup = selectedGroupProp || incomingFromLocation || incomingFromHistory || (selectedGroupId ? { Id: selectedGroupId } : null);
    if (startup) loadGroup(startup);
  }, [selectedGroupProp, selectedGroupId, location]);

  // cleanup created object URLs to avoid memory leaks
  useEffect(() => {
    return () => {
      try { if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current); } catch (e) { }
    };
  }, []);

  // fetch detail and image for a group
  const loadGroup = async (g) => {
    if (!g) return;
    setGroupLoading(true);
    try {
      setSelected(g);
      const id = g.Id || g.id || g.GrupoId;
      if (!id) return;
    // Only set title/desc now if the starter object actually contains them.
    // Otherwise keep current UI values until the GET returns (avoids flashing the placeholder).
    const incomingName = g.NomeGrupo || g.nomeGrupo || g.nome_grupo || g.nome || g.Nome || null;
    if (incomingName) setTitle(incomingName);
    const incomingDesc = (typeof g.Descricao !== 'undefined') ? (g.Descricao || g.descricao || '') : null;
    if (incomingDesc !== null) setDesc(incomingDesc);

    // try GET details
    try {
      const res = await apiCall(`Grupo/${id}`, 'GET');
      const detail = Array.isArray(res) ? (res[0] || null) : res;
      console.debug('group detail raw for id', id, detail);
      if (detail) {
        // ensure selected holds the full detail object so other code can access fields
        try { setSelected(detail); } catch (e) { /* ignore */ }
        console.debug('setting title from detail fields', detail.NomeGrupo, detail.nomeGrupo, detail.nome_grupo, detail.nome, detail.Nome);
        setTitle(detail.NomeGrupo || detail.nomeGrupo || detail.nome_grupo || detail.nome || detail.Nome || title);
        setDesc(detail.Descricao || detail.descricao || desc);
        // also set editable buffer values
        setEditTitle(detail.NomeGrupo || detail.nomeGrupo || detail.nome_grupo || detail.nome || detail.Nome || title);
        setEditDesc(detail.Descricao || detail.descricao || desc);
        
        // Verificar se o usuário logado é o dono do grupo
        const loggedUserId = getUsuarioSistemaId();
        const grupoOwnerId = detail.UsuarioSistemaId || detail.usuarioSistemaId || detail.usuario_sistema_id || detail.DonoGrupo || detail.donoGrupo;
        setDonoId(grupoOwnerId);
        const ownerCheck = loggedUserId && grupoOwnerId && loggedUserId === grupoOwnerId;
        setIsOwner(ownerCheck);
        
        // Verificar se o usuário é admin do grupo (buscar na tabela usuarios_grupo)
        if (!ownerCheck && loggedUserId) {
          try {
            const membrosRes = await apiCall(`UsuariosGrupo/grupo/${id}`, 'GET');
            const membrosArr = Array.isArray(membrosRes) ? membrosRes : [];
            const meuRegistro = membrosArr.find(m => {
              const uid = m.UsuarioSistemaId || m.usuarioSistemaId;
              return uid === loggedUserId;
            });
            if (meuRegistro && (meuRegistro.AdminGrupo === 1 || meuRegistro.adminGrupo === 1)) {
              setIsAdmin(true);
            } else {
              setIsAdmin(false);
            }
          } catch (e) {
            console.debug('Erro ao verificar admin do grupo', e);
            setIsAdmin(false);
          }
        } else {
          setIsAdmin(false);
        }
        
        // clear any previous image
        setImageUrl(null);

        // preferred fields
        const cand = detail.FotoUrl || detail.fotoUrl || detail.fotoGrupo || detail.FotoGrupo || detail.foto_grupo || detail.foto || detail.Foto || detail.Arquivo || detail.arquivo || detail.image || null;
        console.debug('photo candidate for group', id, cand);
        let ok = false;
        if (cand) {
          ok = await trySetImage(cand);
          console.debug('trySetImage returned', ok);
        }
        // if candidate handling failed, do not perform extra legacy network attempts — skip
      }
    } catch (e) {
      console.debug('loadGroup details failed', e);
    }

      // Fetch posts for this group via the dedicated group endpoint
      setPosts([]);
      setPostsLoading(true);
      setPostsError('');
      try {
        const G_PAGE_SIZE = 10;
        let allPosts = [];
        let skip = 0;
        let hasMore = true;
        while (hasMore) {
          const data = await apiCall(`Postagem/grupo/${id}?skip=${skip}&take=${G_PAGE_SIZE}`, 'GET');
          const arr = (data && data.Items) ? data.Items : (Array.isArray(data) ? data : (data?.items || []));
          const total = data && typeof data.Total === 'number' ? data.Total : null;
          allPosts = [...allPosts, ...arr];
          skip += arr.length;
          hasMore = total !== null ? skip < total : arr.length === G_PAGE_SIZE;
          if (!arr.length) break;
        }
        const withDate = allPosts.map(p => ({ ...p, createdAt: extractPostDate(p) }));
        const withFiles = await Promise.all(withDate.map(p => fetchFilesForPost(p)));
        setPosts(withFiles);
        fetchCountsForPosts(withFiles).catch(() => {});
      } catch (err) {
        console.debug('failed to fetch posts for group', err);
        setPostsError('Não foi possível carregar postagens do grupo');
        setPosts([]);
      } finally {
        setPostsLoading(false);
      }
    } finally {
      setGroupLoading(false);
    }
  };

  // cleanup post object URLs on unmount
  React.useEffect(() => {
    return () => {
      try {
        Object.values(postObjectUrlRefs.current || {}).forEach(arr => { (arr || []).forEach(u => { try { URL.revokeObjectURL(u); } catch {} }); });
      } catch (e) {}
    };
  }, []);

  const trySetImage = async (candidate) => {
    try {
      if (!candidate) return false;
      // if it's an array of bytes
      if (Array.isArray(candidate) && candidate.length) {
        const blob = new Blob([Uint8Array.from(candidate)], { type: 'image/jpeg' });
        const url = URL.createObjectURL(blob);
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = url;
        setImageUrl(url);
        return true;
      }
      // object with byte-array fields
      if (typeof candidate === 'object') {
        const bytes = candidate.bytes || candidate.array || candidate.dataBytes || candidate.FotoGrupo;
        if (Array.isArray(bytes) && bytes.length) {
          const blob = new Blob([Uint8Array.from(bytes)], { type: candidate.contentType || 'image/jpeg' });
          const url = URL.createObjectURL(blob);
          if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
          objectUrlRef.current = url;
          setImageUrl(url);
          return true;
        }
        // fall through to string handlers if present
        const p = candidate.url || candidate.file || candidate.data || candidate.base64 || candidate.FotoUrl || candidate.fotoUrl || candidate.arquivo || null;
        if (typeof p === 'string') return await trySetImage(p);
        return false;
      }
      // string handlers (fast, do not prefetch remote URLs)
      if (typeof candidate === 'string') {
        const s = candidate.trim();
        if (!s) return false;
        if (s.startsWith('data:') || s.startsWith('blob:')) { setImageUrl(s); return true; }
        if (/^https?:\/\//i.test(s) || s.startsWith('//')) { setImageUrl(s); return true; }
        // base64-like string
        const cleaned = s.replace(/\s+/g, '');
        const base64like = /^[A-Za-z0-9+/=]+$/.test(cleaned) && cleaned.length > 40;
        if (base64like) { setImageUrl('data:image/jpeg;base64,' + cleaned); return true; }
        return false;
      }
    } catch (e) { console.debug('trySetImage failed', e); }
    return false;
  };

  const onFileChange = async (e) => {
    const f = e.target && e.target.files && e.target.files[0]; if (!f || !selected) return; setUploading(true); setUploadError('');
    try {
      const reader = new FileReader();
      const base64 = await new Promise((res, rej) => { reader.onload = () => { const s = typeof reader.result === 'string' ? reader.result : ''; res(s.includes(',') ? s.split(',')[1] : s); }; reader.onerror = () => rej(new Error('file read error')); reader.readAsDataURL(f); });

      const id = selected.Id || selected.id || selected.GrupoId;

      // Backend AtualizarGrupoDto only updates non-null fields — just send the photo
      const payload = { FotoGrupo: base64 };
      await apiCall(`Grupo/${id}`, 'PUT', payload);

      // Atualiza só a imagem localmente — sem recarregar postagens ou resto da página
      const dataUrl = 'data:image/jpeg;base64,' + base64;
      if (objectUrlRef.current) { try { URL.revokeObjectURL(objectUrlRef.current); } catch {} objectUrlRef.current = null; }
      setImageUrl(dataUrl);

      // notify sidebar to refresh group photo there too
      try { window.dispatchEvent(new CustomEvent('groupsChanged')); } catch {}
    } catch (err) { console.error('upload failed', err); setUploadError('Erro ao enviar imagem'); }
    finally { setUploading(false); try { e.target.value = ''; } catch {} }
  };

  // Edit handlers
  const startEdit = () => { setEditing(true); setEditTitle(title); setEditDesc(desc); setEditError(''); };
  const cancelEdit = () => { setEditing(false); setEditTitle(title); setEditDesc(desc); setEditError(''); };
  const fetchFotoBase64 = null;

  const saveEdit = async () => {
    if (!selected) return;
    setSavingEdit(true); setEditError('');
    try {
      const id = selected.Id || selected.id || selected.GrupoId;

      // Backend AtualizarGrupoDto só atualiza campos não-nulos — envia só o que mudou
      const payload = { NomeGrupo: editTitle, Descricao: editDesc };
      await apiCall(`Grupo/${id}`, 'PUT', payload);

      // Atualiza título e descrição localmente — sem recarregar postagens
      setTitle(editTitle);
      setDesc(editDesc);
      setEditing(false);

      // notify sidebar to refresh
      try { window.dispatchEvent(new CustomEvent('groupsChanged')); } catch {}
    } catch (e) {
      console.error('save edit failed', e); setEditError('Erro ao salvar alterações');
    } finally { setSavingEdit(false); }
  }; 

  return (
    <div className="groups-dropdown-overlay" onClick={(e) => { if (e.target.classList && e.target.classList.contains('groups-dropdown-overlay')) onClose && onClose(); }}>
      {lightboxOpen && (
        <Lightbox images={lbImages} index={lbIndex} onClose={closeLightbox} onNext={lbNext} onPrev={lbPrev} />
      )}
      <div className="groups-dropdown-card" role="dialog" aria-label="Grupos" onClick={(e) => e.stopPropagation()}>
        <LoadingOverlay isLoading={groupLoading} message="Carregando grupo..." />

        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>



          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {!selected ? (
            <div style={{ padding: 24, color: '#666' }}>Nenhum grupo selecionado. Selecione um grupo no sidebar (Home) ou abra este painel a partir de um grupo.</div>
          ) : (
            <div className="grupo-top-container">
              <div className="groups-dropdown-circle" role="button" aria-label="Alterar foto do grupo" onClick={() => { try { fileRef.current && fileRef.current.click(); } catch {} }}>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFileChange} />
                {imageUrl ? (
                  <img src={imageUrl} alt="Foto do grupo" onError={(e) => { console.debug('image load error', imageUrl, e); setImageUrl(null); }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z" fill="rgb(235, 133, 0)"/>
                    </svg>
                  </div>
                )}
              </div>

              <div className="grupo-top-right">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {editing ? (
                    <input className="form-input" value={editTitle} onChange={e => setEditTitle(e.target.value)} placeholder="Título do grupo" style={{ flex: 1 }} />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className="grupo-top-title" style={{ marginRight: 8, flex: '0 1 auto' }}>{title}</div>
                      <button className="grupo-edit-icon" onClick={startEdit} aria-label="Editar título e descrição" title="Editar título e descrição">
                        <svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/></svg>
                      </button>
                    </div>
                  )}
                </div>

                <div className="grupo-top-desc-wrap">
                  {editing ? (
                    <textarea className="form-input" value={editDesc} onChange={e => setEditDesc(e.target.value)} rows={3} placeholder="Descrição do grupo" />
                  ) : (
                    <div className="grupo-top-desc-text">{desc}</div>
                  )}
                </div>

                {editing && (
                  <div className="grupo-edit-actions">
                    <button className="button-create" onClick={saveEdit} disabled={savingEdit}>{savingEdit ? 'Salvando...' : 'Salvar'}</button>
                    <button className="button-create" style={{ background: '#fff', color: 'rgb(235, 133, 0)', border: '1px solid rgb(235, 133, 0)' }} onClick={cancelEdit}>Cancelar</button>
                    {editError && <div style={{ color: '#d32f2f' }}>{editError}</div>}
                  </div>
                )}

                {uploading && <div style={{ color: 'rgb(235, 133, 0)' }}>Enviando imagem...</div>}
                {uploadError && <div style={{ color: '#d32f2f' }}>{uploadError}</div>}
              </div> 
            </div>
          )}

            <div style={{ marginTop: 12, width: '100%', maxWidth: 900 }}>
              {/* Card de Membros - aparece para todos */}
              {selected && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                  <div
                    className="membros-card"
                    onClick={() => setShowMembrosPopup(true)}
                    role="button"
                    tabIndex={0}
                    aria-label="Ver membros do grupo"
                  >
                    <div className="membros-card-icon">
                      {/* Ícone de 3 bonecos - 1 à frente e 2 ao fundo */}
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        {/* Boneco de trás esquerdo */}
                        <circle cx="6" cy="8" r="2.5" fill="rgb(250, 178, 65)"/>
                        <path d="M6 11.5c-2.5 0-4.5 1.5-4.5 3.5v1h9v-1c0-2-2-3.5-4.5-3.5z" fill="rgb(250, 178, 65)"/>
                        {/* Boneco de trás direito */}
                        <circle cx="18" cy="8" r="2.5" fill="rgb(250, 178, 65)"/>
                        <path d="M18 11.5c-2.5 0-4.5 1.5-4.5 3.5v1h9v-1c0-2-2-3.5-4.5-3.5z" fill="rgb(250, 178, 65)"/>
                        {/* Boneco da frente (central) */}
                        <circle cx="12" cy="6" r="3" fill="rgb(235, 133, 0)"/>
                        <path d="M12 10c-3 0-5.5 2-5.5 4.5v1.5h11v-1.5c0-2.5-2.5-4.5-5.5-4.5z" fill="rgb(235, 133, 0)"/>
                      </svg>
                    </div>
                    <span className="membros-card-text">Membros</span>
                  </div>
                </div>
              )}

              <h4 style={{ margin: '6px 0', fontSize: '18px', textAlign: 'center', width: '100%' }}>Publicações</h4>

              {/* Embedded posting form — posts here must be restricted to the currently opened group */}
              {selected && (
                <div className="group-post-card">
                  <FormularioPostagem
                    apiCall={apiCall}
                    apiUploadMultipart={apiUploadMultipart}
                    onPosted={(newPost) => { (async () => {
                        try {
                            // Fetch the real post with server-generated id to attach files
                            const pid = newPost?.id || newPost?.Id || newPost?.PostagemId || newPost?.postagemId;
                            let enriched;
                            if (pid) {
                                try {
                                    const fetched = await apiCall(`Postagem/${pid}`, 'GET');
                                    const withDate = { ...(fetched || newPost), createdAt: extractPostDate(fetched || newPost) };
                                    enriched = await fetchFilesForPost(withDate);
                                } catch (e) {
                                    enriched = await fetchFilesForPost({ ...newPost, createdAt: extractPostDate(newPost) });
                                }
                            } else {
                                enriched = await fetchFilesForPost({ ...newPost, createdAt: extractPostDate(newPost) });
                            }
                            setPosts(prev => [enriched, ...prev]);
                            fetchCountsForPosts([enriched]).catch(() => {});
                        } catch (e) {
                            const item = { ...newPost, createdAt: extractPostDate(newPost) };
                            setPosts(prev => [item, ...prev]);
                        }
                    })(); }}
                    fixedGroup={selected}
                    hideGroupButton={true}
                  />
                </div>
              )}

              {/* Sorting controls for group posts (same behavior as Home.js) */}
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 12 }}>
                <label style={{ fontSize: 13, color: '#666', marginRight: 8 }} htmlFor="group-sort-posts">Ordenar:</label>
                <select id="group-sort-posts" className="sort-select" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} aria-label="Ordenar posts do grupo">
                  <option value="desc">Mais recente</option>
                  <option value="asc">Mais antigo</option>
                  <option value="date">Por data</option>
                </select>
                {sortOrder === 'date' && (
                  <div style={{ marginLeft: 8 }}>
                    <input ref={dateInputRef} type="date" className="date-input" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} aria-label="Filtrar por data" />
                  </div>
                )}
              </div>

              {postsLoading && (
                <div style={{ textAlign: 'center', padding: '24px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <svg width="32" height="32" viewBox="0 0 50 50" style={{ animation: 'grupoSpin 1s linear infinite' }}>
                    <circle cx="25" cy="25" r="20" stroke="rgb(235, 133, 0)" strokeWidth="4" fill="none" strokeLinecap="round" strokeDasharray="31.4 31.4" />
                  </svg>
                  <span style={{ color: '#666', fontSize: 14 }}>Carregando postagens...</span>
                  <style>{`@keyframes grupoSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                </div>
              )}
              {postsError && <div style={{ color: '#d32f2f' }}>{postsError}</div>}

              <div style={{ width: '100%', marginTop: 12 }}>
                {!postsLoading && sortedGroupPosts && sortedGroupPosts.length === 0 && <div style={{ color: '#666' }}>Nenhuma postagem neste grupo.</div>}
                {!postsLoading && sortedGroupPosts && sortedGroupPosts.map((p, idx) => {
                  // Buscar nome do usuário se ainda não temos
                  const postUserId = p.UsuarioSistemaId || p.usuarioSistemaId || p.UsuarioSistema?.Id || p.usuarioSistema?.id;
                  if (postUserId && !userNamesById[postUserId] && !userNamesFetchingRef.current.has(postUserId)) {
                    fetchUserName(postUserId);
                  }
                  const postUserName = getUserName(postUserId);
                  const pid = p.Id || p.id || p.PostagemId || p.postagemId;
                  const imgIdx = imageIndexes[pid] || 0;
                  const images = (p.files || []).filter(f => f && f.type && (f.type.startsWith('image/') || f.type.startsWith('video/')));
                  const nonImages = (p.files || []).filter(f => f && f.type && !f.type.startsWith('image/') && !f.type.startsWith('video/'));
                  const cf = images[imgIdx] || {};

                  const fileEmoji = (f) => {
                    const t = (f.type || '').toLowerCase();
                    const name = (f.name || f.fileName || f.Nome || f.nome || '').toLowerCase();
                    if (t.includes('pdf') || name.endsWith('.pdf')) return '📄';
                    if (t.includes('text') || name.endsWith('.txt')) return '📝';
                    if (name.endsWith('.csv')) return '📑';
                    if (name.endsWith('.doc') || name.endsWith('.docx') || t.includes('word')) return '📄';
                    return '📎';
                  };

                  return (
                    <div className="post-card" key={pid || idx} style={{ position: 'relative' }}>
                      {/* Trash + Pencil icons — only for posts owned by the logged user */}
                      {(() => { const myId = getUsuarioSistemaId(); const ownerId = p.UsuarioSistemaId || p.usuarioSistemaId || p.UsuarioSistema?.Id || p.usuarioSistema?.id; return myId && ownerId && String(myId) === String(ownerId); })() && editingPostId !== pid && (
                        <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 4, zIndex: 2 }}>
                          <button type="button" title="Excluir postagem" aria-label="Excluir postagem" onClick={() => setConfirmDeletePostId(pid)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, lineHeight: 1 }}>
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#e53935" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                          </button>
                          <button type="button" title="Editar postagem" aria-label="Editar postagem" onClick={() => startEditPost(p)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, lineHeight: 1 }}>
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="rgb(235, 133, 0)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                        </div>
                      )}
                      {/* Author + date */}
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                        {postUserName && <div className="post-author" style={{ fontWeight: 'bold', color: '#333' }}>{postUserName}</div>}
                        <div className="post-time">{formatPostDate(p.createdAt)}</div>
                      </div>
                      {(p.Titulo || p.title || p.titulo) && <div className="post-title">{p.Titulo || p.title || p.titulo}</div>}

                      {/* description or edit mode */}
                      {editingPostId === pid ? (
                        <div style={{ marginBottom: 8 }}>
                          <textarea value={editingText} onChange={(e) => setEditingText(e.target.value)} style={{ width: '100%', minHeight: 80, padding: 8, borderRadius: 6, border: '1px solid #ddd', fontSize: 14, resize: 'vertical' }} disabled={editingSaving} />

                          {/* Edit-mode image thumbnails */}
                          <div style={{ marginTop: 10 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 6 }}>Imagens</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                              {editImages.map((img, i) => (
                                <div key={img.id || img._tempUrl || i} style={{ position: 'relative', width: 64, height: 64, borderRadius: 6, overflow: 'hidden', border: '1px solid #ddd' }}>
                                  {img.type && img.type.startsWith('video/') ? (
                                    <video src={img.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  ) : (
                                    <img src={img.url} alt={img.name || `img-${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
                                  )}
                                  <button type="button" onClick={() => editRemoveImage(i)} style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, lineHeight: 1 }} title="Remover imagem">
                                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><line x1="2" y1="2" x2="10" y2="10"/><line x1="10" y1="2" x2="2" y2="10"/></svg>
                                  </button>
                                </div>
                              ))}
                              {/* Add image button */}
                              <div onClick={() => editImageInputRef.current && editImageInputRef.current.click()} style={{ width: 64, height: 64, borderRadius: 6, border: '2px dashed rgb(235, 133, 0)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#fff' }} title="Adicionar imagem">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgb(235, 133, 0)" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                              </div>
                              <input ref={editImageInputRef} type="file" accept="image/*,video/*" multiple style={{ display: 'none' }} onChange={(e) => { if (e.target.files && e.target.files.length > 0) { editAddImages(e.target.files); e.target.value = ''; } }} />
                            </div>
                          </div>

                          {/* Edit-mode non-image files */}
                          <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 6 }}>Arquivos</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {editFiles.map((f, i) => {
                                const fname = f.NomeArquivo || f.nome || f.Nome || f.fileName || f.name || `Arquivo ${i+1}`;
                                const t = (f.type || '').toLowerCase();
                                const n = (fname).toLowerCase();
                                const emoji = (t.includes('pdf') || n.endsWith('.pdf')) ? '📄' : (t.includes('text') || n.endsWith('.txt')) ? '📝' : n.endsWith('.csv') ? '📑' : '📎';
                                return (
                                  <div key={f.id || f._tempUrl || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', background: '#f9f9f9', borderRadius: 6, border: '1px solid #eee', position: 'relative' }}>
                                    <span style={{ fontSize: 16 }}>{emoji}</span>
                                    <span style={{ flex: 1, fontSize: 13, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fname}</span>
                                    <button type="button" onClick={() => editRemoveFile(i)} style={{ background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, lineHeight: 1, flexShrink: 0 }} title="Remover arquivo">
                                      <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><line x1="2" y1="2" x2="10" y2="10"/><line x1="10" y1="2" x2="2" y2="10"/></svg>
                                    </button>
                                  </div>
                                );
                              })}
                              {/* Add file button */}
                              <div onClick={() => editFileInputRef.current && editFileInputRef.current.click()} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 6, border: '2px dashed rgb(235, 133, 0)', cursor: 'pointer', background: '#fff', justifyContent: 'center' }} title="Adicionar arquivo">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgb(235, 133, 0)" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                <span style={{ color: 'rgb(235, 133, 0)', fontWeight: 600, fontSize: 13 }}>Adicionar arquivo</span>
                              </div>
                              <input ref={editFileInputRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => { if (e.target.files && e.target.files.length > 0) { editAddFiles(e.target.files); e.target.value = ''; } }} />
                            </div>
                          </div>

                          {/* Save / Cancel */}
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
                            <button onClick={cancelEditPost} className="button-create" style={{ background: '#9e9e9e', fontSize: 13, padding: '6px 14px' }} disabled={editingSaving}>Cancelar</button>
                            <button onClick={() => saveEditPost(pid)} className="button-create" style={{ fontSize: 13, padding: '6px 14px' }} disabled={editingSaving}>{editingSaving ? 'Salvando...' : 'Salvar'}</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {(p.Descricao || p.descricao || p.description) && (
                            <div className="post-description">{p.Descricao || p.descricao || p.description}</div>
                          )}
                        </>
                      )}

                      {/* Image gallery */}
                      {images.length > 0 && (
                        <div>
                          <div className={`post-image-wrap ${cf.isSquare ? 'square' : cf.isPortrait ? 'portrait' : ''}`}>
                            {images.length > 1 && (
                              <div className="post-image-arrow left" role="button" title="Anterior"
                                onClick={() => setImageIndexes(prev => ({ ...prev, [pid]: (imgIdx - 1 + images.length) % images.length }))}>◀</div>
                            )}
                            {cf && cf.url ? (
                              cf.type && cf.type.startsWith('video/') ? (
                                <div className="post-video-wrap"><video src={cf.url} className={`post-video ${cf.isSquare ? 'square' : cf.isPortrait ? 'portrait' : ''}`} controls /></div>
                              ) : (
                                <img
                                  className={`post-main-image ${cf.isSquare ? 'square' : cf.isPortrait ? 'portrait' : ''}`}
                                  src={cf.url} alt="Post image" loading="lazy"
                                  onClick={() => { const imgs = images.map(f => ({ url: f.url, type: f.type })); openLightbox(imgs, imgIdx); }}
                                  onError={e => { e.target.style.display = 'none'; }}
                                />
                              )
                            ) : null}
                            {images.length > 1 && (
                              <div className="post-image-arrow right" role="button" title="Próxima"
                                onClick={() => setImageIndexes(prev => ({ ...prev, [pid]: (imgIdx + 1) % images.length }))}>▶</div>
                            )}
                            {images.length > 1 && <div className="post-image-count">{imgIdx + 1} / {images.length}</div>}
                          </div>
                          {images.length > 1 && (
                            <div className="post-thumbs">
                              {images.map((f, ti) => (
                                <div key={ti} className={`post-thumb ${imgIdx === ti ? 'active' : ''}`}
                                  onClick={() => setImageIndexForPost(pid, ti)}>
                                  {f.type && f.type.startsWith('video/') ? (
                                    <video src={f.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  ) : (
                                    <img src={f.url} alt={`thumb-${ti}`} loading="lazy" onError={e => { e.target.style.display = 'none'; }} />
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Non-image files */}
                      {nonImages.length > 0 && (
                        <div className="files-preview" style={{ marginTop: 12 }}>
                          <div className="files-row files-row-scrollable">
                            {nonImages.map((f, fi) => {
                              const lowerType = (f.type || '').toLowerCase();
                              const lowerName = (f.name || f.NomeArquivo || '').toLowerCase();
                              const isPdf = lowerType.includes('pdf') || lowerName.endsWith('.pdf');
                              const displayName = isPdf
                                ? (f.NomeArquivo || f.nome || f.Nome || f.fileName || f.name) || ''
                                : (f.nome || f.Nome || f.fileName || f.name) || (f.type || '').split('/').pop() || `Arquivo ${fi + 1}`;
                              const downloadName = (f.NomeArquivo || f.nome || f.Nome || f.fileName || f.name) || `Arquivo-${fi + 1}`;
                              return (
                                <div key={f.id || fi} className="file-item">
                                  <div style={{ fontSize: 18 }}>{fileEmoji(f)}</div>
                                  <a className="file-title" href={f.url} download={downloadName} target="_blank" rel="noopener noreferrer">{displayName}</a>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Likes / Comments counts */}
                      <div style={{ display: 'flex', gap: 12, marginTop: 10, marginBottom: 4, fontSize: 13, color: '#888' }}>
                        {(likesCountByPost[pid] || 0) > 0 && <span>{likesCountByPost[pid]} curtida{(likesCountByPost[pid] || 0) !== 1 ? 's' : ''}</span>}
                        {(commentsCountByPost[pid] || 0) > 0 && <span>{commentsCountByPost[pid]} comentário{(commentsCountByPost[pid] || 0) !== 1 ? 's' : ''}</span>}
                      </div>

                      {/* Action buttons */}
                      <div className="post-actions">
                        <button type="button" className={`action-btn like-btn ${likedPosts.has(pid) ? 'liked' : ''}`}
                          onClick={() => toggleLike(p)} aria-pressed={likedPosts.has(pid)}>
                          <svg className="icon heart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />
                          </svg>
                          <span>Curtir</span>
                        </button>
                        <button type="button" className={`action-btn comments-btn ${commentsOpenPosts.has(pid) ? 'open' : ''}`}
                          onClick={() => toggleComments(p)}>
                          <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                          <span>Comentários</span>
                        </button>
                      </div>

                      {/* Comments section */}
                      {commentsOpenPosts.has(pid) && (
                        <div className="comments-section">
                          {commentsLoadingPosts.has(pid) && <div style={{ color: '#999', fontSize: 13 }}>Carregando comentários...</div>}
                          <div className="comment-input-row">
                            <input className="comment-input" type="text" placeholder="Escreva um comentário..."
                              value={commentInputs[pid] || ''}
                              onChange={e => setCommentInputs(prev => ({ ...prev, [pid]: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment(pid); } }} />
                            <button type="button" className="comment-button" onClick={() => submitComment(pid)}>Comentar</button>
                          </div>
                          {(commentsByPost[pid] || []).length > 0 && (
                            <div className="comment-list">
                              {(commentsByPost[pid] || []).map(c => {
                                const cUserId = c.UsuarioSistemaId || c.usuarioSistemaId;
                                if (cUserId && !userNamesById[cUserId] && !userNamesFetchingRef.current.has(cUserId)) fetchUserName(cUserId);
                                const cName = getUserName(cUserId) || 'Usuário';
                                return (
                                  <div key={c.id || c.Id} className="comment-item">
                                    <div className="comment-header"><strong>{cName}</strong><span className="comment-time">{c.DataHora ? formatPostDateMinutes(c.DataHora) : ''}</span></div>
                                    <div className="comment-text">{c.conteudo || c.text || c.Conteudo}</div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Delete confirmation modal */}
                      {confirmDeletePostId === pid && (
                        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setConfirmDeletePostId(null)}>
                          <div style={{ background: '#fff', borderRadius: 14, padding: '28px 32px', minWidth: 320, maxWidth: 420, boxShadow: '0 8px 40px rgba(0,0,0,0.18)', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 10 }}>Excluir publicação</div>
                            <div style={{ color: '#666', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>Essa ação não pode ser desfeita.</div>
                            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                              <button className="button-create" style={{ background: '#9e9e9e', fontSize: 15, padding: '10px 24px', minWidth: 120 }} onClick={() => setConfirmDeletePostId(null)}>Cancelar</button>
                              <button className="button-create" style={{ background: '#d32f2f', fontSize: 15, padding: '10px 24px', minWidth: 120 }} onClick={() => deletePost(pid)}>Excluir</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

          </div>

        </div>

      </div>

      {/* Popup de Membros */}
      {showMembrosPopup && selected && (
        <MembrosGrupoPopup
          grupoId={selected.Id || selected.id || selected.GrupoId}
          donoId={donoId}
          isOwner={isOwner}
          isAdmin={isAdmin}
          onClose={() => setShowMembrosPopup(false)}
          onMembersChanged={() => { /* pode recarregar dados se necessário */ }}
        />
      )}

      {/* Popup de Gerenciar Admin */}
      {showGerenciarAdminPopup && selected && (
        <GerenciarAdminPopup
          grupoId={selected.Id || selected.id || selected.GrupoId}
          donoId={donoId}
          isOwner={isOwner}
          onClose={() => setShowGerenciarAdminPopup(false)}
          onAdminsChanged={() => { /* pode recarregar dados se necessário */ }}
        />
      )}
    </div>
  );
}
